import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Shield, Bell, LogOut, ChevronRight, CheckCircle2, User, Mail, Phone, Calendar, Copy, Gift, ArrowRight, Loader2, Coins, HelpCircle, Pencil, ExternalLink, Building2, CreditCard, Share2, Ticket, Camera, Upload, Trash2, Save, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useGoldRate } from '../../context/GoldRateContext';
import { useNotification } from '../../context/NotificationContext';
import { authAPI, userAPI } from '../../services/api';
import api from '../../services/api';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import ReferralShareDialog from './ReferralShareDialog';
import { AddressField } from './AddressField';

const ProfileDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, login, logout, isAuthenticated, refreshUser } = useAuth();
  const { sellRate } = useGoldRate();
  const { openNotifications } = useNotification();
  const [isAnimating, setIsAnimating] = useState(false);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile Picture state
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Profile Edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    dob: '',
    address: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Login state
  const [loginStep, setLoginStep] = useState('phone'); // phone, otp, name
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [testOtpNote, setTestOtpNote] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  
  // Bank Details state
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankForm, setBankForm] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    bankName: '',
    consent: false
  });
  const [bankLoading, setBankLoading] = useState(false);
  const [ifscLoading, setIfscLoading] = useState(false);
  const [ifscError, setIfscError] = useState('');
  
  // Referral Share Dialog state
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [referralShareData, setReferralShareData] = useState(null);
  
  // Apply Referral Code state
  const [applyReferralCode, setApplyReferralCode] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [hasAppliedReferral, setHasAppliedReferral] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Reset login state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setLoginStep('phone');
      setPhone('');
      setOtp('');
      setName('');
      setTestOtpNote('');
      setPendingUser(null);
      setPendingToken(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // ===== LOGIN METHODS (reused from LoginPage) =====
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, 10);
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.sendOTP(phone);
      setLoginStep('otp');
      setCountdown(60);
      toast.success('OTP sent successfully!');
      if (response.data.note) {
        setTestOtpNote(response.data.note);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (value) => {
    if (value.length !== 6) return;
    
    setLoading(true);
    try {
      const response = await authAPI.verifyOTP(phone, value);
      const { user: userData, session_token, is_new_user, needs_name_update } = response.data;
      
      if (is_new_user || needs_name_update) {
        setPendingUser(userData);
        setPendingToken(session_token);
        setLoginStep('name');
        toast.info('Please enter your name as per Aadhaar');
      } else {
        login(userData, session_token);
        toast.success('Login successful!');
        // Reset login state
        setLoginStep('phone');
        setPhone('');
        setOtp('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) {
      toast.error('Please enter your full name (minimum 2 characters)');
      return;
    }

    setLoading(true);
    try {
      // First login with pending credentials
      login(pendingUser, pendingToken);
      
      // Then update the name
      await userAPI.updateProfile({ name: name.trim() });
      toast.success('Welcome to Mint a Milli!');
      
      // Reset login state
      setLoginStep('phone');
      setPhone('');
      setOtp('');
      setName('');
      setPendingUser(null);
      setPendingToken(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      const response = await authAPI.sendOTP(phone);
      setCountdown(60);
      toast.success('OTP resent successfully!');
      if (response.data.note) {
        setTestOtpNote(response.data.note);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };
  // ===== END LOGIN METHODS =====

  // ===== BANK DETAILS METHODS =====
  // Use bank_account_last4 for checking if bank details exist (PII protection)
  const hasBankDetails = !!user?.bank_account_last4 || !!user?.bank_account_number;
  // Use the last4 field for display (masked on backend now)
  const maskedAccountNumber = user?.bank_account_last4 
    ? `****${user.bank_account_last4}` 
    : (user?.bank_account_number ? `****${user.bank_account_number.slice(-4)}` : null);

  const validateIFSC = (ifsc) => {
    // IFSC format: 4 letters + 0 + 6 alphanumeric
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc.toUpperCase());
  };

  const validateAccountNumber = (accNum) => {
    // Account numbers are typically 9-18 digits
    return /^\d{9,18}$/.test(accNum);
  };

  const handleBankFormChange = (field, value) => {
    if (field === 'ifscCode') {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
      // Auto-lookup when IFSC is complete (11 characters)
      if (value.length === 11 && validateIFSC(value)) {
        lookupIFSC(value);
      } else {
        setIfscError('');
      }
    } else if (field === 'accountNumber' || field === 'confirmAccountNumber') {
      value = value.replace(/\D/g, '').slice(0, 18);
    }
    setBankForm(prev => ({ ...prev, [field]: value }));
  };

  // IFSC Auto-Lookup using Razorpay API
  const lookupIFSC = async (ifsc) => {
    setIfscLoading(true);
    setIfscError('');
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (response.ok) {
        const data = await response.json();
        setBankForm(prev => ({ 
          ...prev, 
          bankName: data.BANK || '',
          branchName: data.BRANCH || ''
        }));
        toast.success(`Bank found: ${data.BANK}`);
      } else {
        setIfscError('Invalid IFSC code. Please check and try again.');
        setBankForm(prev => ({ ...prev, bankName: '' }));
      }
    } catch (error) {
      console.error('IFSC lookup error:', error);
      setIfscError('Could not verify IFSC. Please enter bank name manually.');
    } finally {
      setIfscLoading(false);
    }
  };

  const openBankModal = () => {
    // Never pre-fill account number for security - user must re-enter
    // Only pre-fill IFSC and bank name as they are not sensitive
    if (hasBankDetails) {
      setBankForm({
        accountNumber: '', // Don't pre-fill - requires fresh entry for security
        confirmAccountNumber: '',
        ifscCode: user.bank_ifsc || '',
        bankName: user.bank_name || '',
        consent: true // Already consented if they have bank details
      });
    } else {
      setBankForm({
        accountNumber: '',
        confirmAccountNumber: '',
        ifscCode: '',
        bankName: '',
        consent: false
      });
    }
    setIfscError('');
    setShowBankModal(true);
  };

  const handleSaveBankDetails = async () => {
    // Validation
    if (!bankForm.accountNumber) {
      toast.error('Please enter account number');
      return;
    }
    if (!validateAccountNumber(bankForm.accountNumber)) {
      toast.error('Account number must be 9-18 digits');
      return;
    }
    if (bankForm.accountNumber !== bankForm.confirmAccountNumber) {
      toast.error('Account numbers do not match');
      return;
    }
    if (!bankForm.ifscCode) {
      toast.error('Please enter IFSC code');
      return;
    }
    if (!validateIFSC(bankForm.ifscCode)) {
      toast.error('Invalid IFSC code format (e.g., HDFC0001234)');
      return;
    }
    if (!bankForm.bankName.trim()) {
      toast.error('Please enter bank name');
      return;
    }
    if (!bankForm.consent) {
      toast.error('Please provide consent to store your bank details');
      return;
    }

    setBankLoading(true);
    try {
      await api.put('/user/bank-details', {
        bank_account_number: bankForm.accountNumber,
        bank_ifsc: bankForm.ifscCode.toUpperCase(),
        bank_name: bankForm.bankName.trim(),
        consent_given: true,
        consent_timestamp: new Date().toISOString()
      });
      
      toast.success('Bank details saved successfully!');
      setShowBankModal(false);
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Bank details error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setBankLoading(false);
    }
  };

  // Delete bank details
  const handleDeleteBankDetails = async () => {
    if (!window.confirm('Are you sure you want to delete your bank details? This cannot be undone.')) {
      return;
    }
    
    setBankLoading(true);
    try {
      await api.delete('/user/bank-details');
      toast.success('Bank details deleted successfully');
      setShowBankModal(false);
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Delete bank details error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete bank details');
    } finally {
      setBankLoading(false);
    }
  };
  // ===== END BANK DETAILS METHODS =====

  // Get user initial for avatar
  const userInitial = user?.name?.charAt(0)?.toUpperCase() || user?.phone?.charAt(0) || 'U';
  const userName = user?.name || 'User';
  const userPhone = user?.phone || '+91 98765 43210';
  const userEmail = user?.email || 'user@example.com';
  const userDOB = user?.date_of_birth || '12 May 1998';
  const milliBalance = user?.milli_balance || 0;
  const referralCode = user?.referral_code || 'MILLI' + (user?.phone?.slice(-4) || '0000');

  // Check if user can apply a referral code (no completed transactions yet)
  const canApplyReferral = !user?.referred_by && !user?.first_transaction_at && !hasAppliedReferral;

  // Copy referral code to clipboard
  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopiedReferral(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedReferral(false), 2000);
  };

  // Open referral share dialog
  const openReferralShareDialog = async () => {
    try {
      // Fetch fresh referral data from journey-stats which includes share_message and share_url
      const response = await api.get('/user/journey-stats');
      setReferralShareData({
        referralCode: response.data.referral_code || referralCode,
        shareMessage: response.data.share_message,
        shareUrl: response.data.share_url,
      });
      setShowReferralDialog(true);
    } catch (error) {
      // Fallback to basic data
      setReferralShareData({
        referralCode: referralCode,
        shareMessage: null,
        shareUrl: null,
      });
      setShowReferralDialog(true);
    }
  };

  // Apply referral code
  const handleApplyReferralCode = async () => {
    if (!applyReferralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplyingReferral(true);
    try {
      const response = await api.post('/referral/apply', {
        referral_code: applyReferralCode.trim().toUpperCase()
      });
      
      toast.success(response.data.message || 'Referral code applied successfully!');
      setHasAppliedReferral(true);
      setApplyReferralCode('');
      
      // Refresh user data to reflect the applied referral
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Apply referral error:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplyingReferral(false);
    }
  };

  // Calculate INR value of Milli balance
  const milliValueINR = milliBalance * (sellRate || 14);
  
  // App version
  const appVersion = 'v1.2.0';

  // Format phone number for display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '+91 98765 43210';
    if (phone.startsWith('+91')) return phone;
    return `+91 ${phone}`;
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  // ===== PROFILE PICTURE METHODS =====
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await userAPI.updateProfilePicture(reader.result);
          toast.success('Profile picture updated');
          await refreshUser();
          setShowPhotoOptions(false);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to update profile picture');
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadingPhoto(false);
      toast.error('Failed to process image');
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await userAPI.deleteProfilePicture();
      toast.success('Profile picture removed');
      await refreshUser();
      setShowPhotoOptions(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove profile picture');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ===== PROFILE EDIT METHODS =====
  const startEditingProfile = () => {
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      dob: user?.date_of_birth || '',
      address: user?.address || ''
    });
    setIsEditingProfile(true);
  };

  const cancelEditingProfile = () => {
    setIsEditingProfile(false);
    setEditForm({ name: '', email: '', dob: '', address: '' });
  };

  const saveProfile = async () => {
    if (!editForm.name.trim() || editForm.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    const address = editForm.address.trim();
    if (!user?.kyc_verified && address && address.length < 10) {
      toast.error('Please enter your complete address');
      return;
    }

    setSavingProfile(true);
    try {
      await userAPI.updateBasicDetails({
        name: editForm.name.trim(),
        email: editForm.email.trim() || null,
        date_of_birth: editForm.dob || null,
        ...(user?.kyc_verified ? {} : { address: address || null })
      });
      toast.success('Profile updated successfully');
      await refreshUser();
      setIsEditingProfile(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) or string errors
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
        toast.error(errorMsg || 'Validation failed');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to update profile');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  // Champagne Gold color palette
  const champagneGold = {
    primary: '#D4AF37',      // Main champagne gold
    light: '#E8D48A',        // Lighter shade
    lighter: '#F5E6C8',      // Very light (background)
    dark: '#B8960C',         // Darker shade
    darker: '#8B7500',       // Very dark
    accent: '#C9A227',       // Accent
  };

  const quickActions = [
    { 
      icon: Shield, 
      label: 'KYC Verification', 
      onClick: () => { handleClose(); navigate('/kyc-new'); }, 
      badge: user?.kyc_status === 'verified' ? 'Verified' : null,
      iconBg: 'bg-[#E8D48A]/50',
      iconColor: 'text-[#8B7500]',
    },
    { 
      icon: Building2, 
      label: 'Bank Details', 
      onClick: openBankModal, 
      badge: hasBankDetails ? 'Added' : 'Required',
      badgeType: hasBankDetails ? 'success' : 'warning',
      iconBg: 'bg-[#D4AF37]/20',
      iconColor: 'text-[#B8960C]',
    },
    { 
      icon: Bell, 
      label: 'Notifications', 
      onClick: () => { handleClose(); openNotifications(); }, 
      iconBg: 'bg-[#C9A227]/20',
      iconColor: 'text-[#8B7500]',
    },
    { 
      icon: HelpCircle, 
      label: 'Help & Support', 
      onClick: () => { handleClose(); navigate('/help-new'); }, 
      iconBg: 'bg-[#F5E6C8]',
      iconColor: 'text-[#B8960C]',
    },
  ];

  if (!isOpen && !isAnimating) return null;

  // ===== LOGIN UI =====
  const renderLoginUI = () => (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
          {loginStep === 'phone' && 'Welcome'}
          {loginStep === 'otp' && 'Verify OTP'}
          {loginStep === 'name' && 'Your Name'}
        </h2>
        <button 
          onClick={handleClose}
          className="p-2 hover:bg-[#004D40]/10 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-[#004D40]" />
        </button>
      </div>

      {/* Login Content */}
      <div className="flex-1 flex flex-col">
        {/* Phone Step */}
        {loginStep === 'phone' && (
          <form onSubmit={handleSendOTP} className="flex flex-col gap-4">
            <div className="text-center mb-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#004D40] to-[#00302B] rounded-full flex items-center justify-center mb-4 shadow-lg ring-4 ring-[#B8860B]/30">
                <Phone className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-[#004D40] mb-1">Login or Register</h3>
              <p className="text-sm text-[#B8860B]">Enter your phone number to get started</p>
            </div>

            {/* First-time user notice */}
            <div className="bg-[#F5E6C8]/50 border border-[#B8860B]/30 rounded-xl p-3 mb-2">
              <p className="text-xs text-[#004D40] leading-relaxed">
                <span className="font-semibold">New here?</span> Registration is simple — just your mobile number and OTP verification. No passwords needed!
              </p>
            </div>

            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#004D40] font-medium">+91</span>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="Enter 10-digit number"
                className="w-full pl-14 pr-4 py-4 text-lg border-2 border-[#B8860B]/40 rounded-xl focus:outline-none focus:border-[#004D40] bg-white text-[#004D40]"
                maxLength={10}
              />
            </div>

            <button
              type="submit"
              disabled={loading || phone.length !== 10}
              className="w-full py-4 bg-gradient-to-r from-[#004D40] to-[#00302B] text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:from-[#00302B] hover:to-[#001A15] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Get OTP <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Terms note */}
            <p className="text-xs text-center text-gray-500 leading-relaxed">
              By continuing, you agree to our{' '}
              <a 
                href="/terms-new" 
                target="_blank" 
                className="text-[#004D40] font-medium underline hover:text-[#00302B]"
              >
                Terms & Conditions
              </a>
              {' '}and{' '}
              <a 
                href="/privacy-new" 
                target="_blank" 
                className="text-[#004D40] font-medium underline hover:text-[#00302B]"
              >
                Privacy Policy
              </a>
            </p>
          </form>
        )}

        {/* OTP Step */}
        {loginStep === 'otp' && (
          <div className="flex flex-col gap-4">
            <div className="text-center mb-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#004D40] to-[#00302B] rounded-full flex items-center justify-center mb-4 shadow-lg ring-4 ring-[#B8860B]/30">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-[#004D40] mb-1">Enter OTP</h3>
              <p className="text-sm text-[#B8860B]">Sent to +91 {phone}</p>
              {testOtpNote && (
                <p className="text-xs text-[#004D40] mt-2 bg-[#F5E6C8] p-2 rounded-lg border border-[#B8860B]/30">{testOtpNote}</p>
              )}
            </div>

            <div className="flex justify-center mb-4">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  if (value.length === 6) {
                    handleVerifyOTP(value);
                  }
                }}
                disabled={loading}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="w-12 h-14 text-xl border-[#B8860B]/40 text-[#004D40]" />
                  <InputOTPSlot index={1} className="w-12 h-14 text-xl border-[#B8860B]/40 text-[#004D40]" />
                  <InputOTPSlot index={2} className="w-12 h-14 text-xl border-[#B8860B]/40 text-[#004D40]" />
                  <InputOTPSlot index={3} className="w-12 h-14 text-xl border-[#B8860B]/40 text-[#004D40]" />
                  <InputOTPSlot index={4} className="w-12 h-14 text-xl border-[#B8860B]/40 text-[#004D40]" />
                  <InputOTPSlot index={5} className="w-12 h-14 text-xl border-[#B8860B]/40 text-[#004D40]" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {loading && (
              <div className="flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#004D40]" />
              </div>
            )}

            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={countdown > 0 || loading}
                className="text-[#B8860B] font-medium disabled:text-gray-400 hover:text-[#A67C00]"
              >
                {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
              </button>
            </div>

            <button
              onClick={() => { setLoginStep('phone'); setOtp(''); }}
              className="text-[#004D40]/70 text-sm hover:text-[#004D40]"
            >
              ← Change phone number
            </button>
          </div>
        )}

        {/* Name Step */}
        {loginStep === 'name' && (
          <form onSubmit={handleNameSubmit} className="flex flex-col gap-4">
            <div className="text-center mb-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#004D40] to-[#00302B] rounded-full flex items-center justify-center mb-4 shadow-lg ring-4 ring-[#B8860B]/30">
                <User className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-[#004D40] mb-1">What&apos;s your name?</h3>
              <p className="text-sm text-[#B8860B]">Enter your name as per Aadhaar</p>
            </div>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              className="w-full px-4 py-4 text-lg border-2 border-[#B8860B]/40 rounded-xl focus:outline-none focus:border-[#004D40] bg-white text-[#004D40]"
            />

            <button
              type="submit"
              disabled={loading || name.trim().length < 2}
              className="w-full py-4 bg-gradient-to-r from-[#004D40] to-[#00302B] text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:from-[#00302B] hover:to-[#001A15] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continue <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  // ===== PROFILE UI =====
  const renderProfileUI = () => (
    <div className="h-full flex flex-col">
      {/* Fixed Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-6 pb-4 border-b border-[#B8860B]/30">
        <h2 className="text-xl font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
          My Profile
        </h2>
        <button 
          onClick={handleClose}
          className="p-2 hover:bg-[#004D40]/10 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-[#004D40]" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 pt-4">
        {/* Hidden file input for photo upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoUpload}
          className="hidden"
        />

        {/* User Card */}
        <div className="bg-gradient-to-r from-[#004D40]/10 to-[#004D40]/5 rounded-xl p-4 border border-[#004D40]/30 mb-5 animate-in fade-in slide-in-from-left-4 duration-300 delay-75">
          <div className="flex items-center gap-4">
            {/* Avatar with upload option */}
            <div className="relative">
              <div 
                className="w-16 h-16 rounded-full bg-gradient-to-br from-[#004D40] to-[#00302B] flex items-center justify-center shadow-lg ring-2 ring-[#B8860B]/50 overflow-hidden cursor-pointer"
                onClick={() => setShowPhotoOptions(!showPhotoOptions)}
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : user?.profile_picture ? (
                  <img 
                    src={user.profile_picture} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-white">{userInitial}</span>
                )}
              </div>
              {/* Camera badge */}
              <button 
                onClick={() => setShowPhotoOptions(!showPhotoOptions)}
                className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#B8860B] rounded-full flex items-center justify-center shadow-md hover:bg-[#8B6914] transition-colors"
              >
                <Camera className="w-3 h-3 text-white" />
              </button>
              
              {/* Photo options dropdown */}
              {showPhotoOptions && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-10 min-w-[140px]">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Photo
                  </button>
                  {user?.profile_picture && (
                    <button
                      onClick={handleRemovePhoto}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove Photo
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Basic Info */}
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#004D40]">{userName}</h3>
              <p className="text-sm text-[#B8860B]">{formatPhoneDisplay(userPhone)}</p>
            </div>
          </div>
          
          {/* Milli Balance */}
          <div className="mt-4 pt-4 border-t border-[#B8860B]/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B8860B] to-[#DAA520] flex items-center justify-center shadow-md">
                  <Coins className="w-4 h-4 text-white" />
                </div>
                <p className="text-xs text-[#004D40]">Your Milli Balance</p>
              </div>
              <button 
                onClick={() => { handleClose(); navigate('/transaction-history'); }}
                className="text-xs font-medium text-[#004D40] hover:text-[#00302B] flex items-center gap-1 bg-[#B8860B]/20 px-3 py-1.5 rounded-full border border-[#B8860B]/40 hover:bg-[#B8860B]/30 transition-colors"
              >
                View Wallet <ChevronRight className="w-3 h-3" />
              </button>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold text-[#004D40]">
                {milliBalance.toFixed(2)} <span className="text-sm font-normal text-[#B8860B]">Milli&apos;s</span>
              </p>
              <p className="text-sm text-[#B8860B] font-medium">≈ ₹{milliValueINR.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Personal Details */}
        <div className="bg-white/80 rounded-xl p-4 border border-[#B8860B]/30 mb-5 animate-in fade-in slide-in-from-left-4 duration-300 delay-100">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-[#004D40]">Personal Information</h4>
            {isEditingProfile ? (
              <div className="flex items-center gap-2">
                <button 
                  onClick={cancelEditingProfile}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004D40] hover:bg-[#00302B] text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50"
                >
                  {savingProfile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save
                </button>
              </div>
            ) : (
              <button 
                onClick={startEditingProfile}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004D40]/10 hover:bg-[#004D40]/20 text-[#004D40] rounded-full text-xs font-medium transition-colors border border-[#004D40]/20"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            )}
          </div>

          {/* KYC Notice */}
          {isEditingProfile && (
            <div className="flex items-start gap-2 mb-4 p-3 bg-[#F5E6C8]/50 rounded-lg border border-[#B8860B]/30">
              <AlertCircle className="w-4 h-4 text-[#B8860B] shrink-0 mt-0.5" />
              <p className="text-xs text-[#004D40]">
                <strong>Note:</strong> Your details should match your KYC documents (Aadhaar/PAN) for verification purposes.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {/* Full Name */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#B8860B]/10 flex items-center justify-center border border-[#B8860B]/20 shrink-0">
                <User className="w-4 h-4 text-[#B8860B]" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#B8860B]">Full Name</p>
                {isEditingProfile ? (
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-2 py-1 text-sm font-medium text-[#004D40] border border-[#B8860B]/40 rounded focus:outline-none focus:ring-1 focus:ring-[#004D40]"
                    placeholder="Enter your name"
                  />
                ) : (
                  <p className="text-sm font-medium text-[#004D40]">{userName}</p>
                )}
              </div>
            </div>

            {/* Mobile Number (not editable) */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#B8860B]/10 flex items-center justify-center border border-[#B8860B]/20 shrink-0">
                <Phone className="w-4 h-4 text-[#B8860B]" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#B8860B]">Mobile Number</p>
                <p className="text-sm font-medium text-[#004D40]">{formatPhoneDisplay(userPhone)}</p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#B8860B]/10 flex items-center justify-center border border-[#B8860B]/20 shrink-0">
                <Mail className="w-4 h-4 text-[#B8860B]" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#B8860B]">Email</p>
                {isEditingProfile ? (
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-2 py-1 text-sm font-medium text-[#004D40] border border-[#B8860B]/40 rounded focus:outline-none focus:ring-1 focus:ring-[#004D40]"
                    placeholder="Enter your email"
                  />
                ) : (
                  <p className="text-sm font-medium text-[#004D40]">{userEmail}</p>
                )}
              </div>
            </div>

            {/* Date of Birth */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#B8860B]/10 flex items-center justify-center border border-[#B8860B]/20 shrink-0">
                <Calendar className="w-4 h-4 text-[#B8860B]" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#B8860B]">Date of Birth</p>
                {isEditingProfile ? (
                  <input
                    type="date"
                    value={editForm.dob}
                    onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
                    className="w-full px-2 py-1 text-sm font-medium text-[#004D40] border border-[#B8860B]/40 rounded focus:outline-none focus:ring-1 focus:ring-[#004D40]"
                  />
                ) : (
                  <p className="text-sm font-medium text-[#004D40]">{userDOB}</p>
                )}
              </div>
            </div>

            <AddressField
              isEditing={isEditingProfile}
              value={editForm.address || ''}
              onChange={(address) => setEditForm({ ...editForm, address })}
              savedAddress={user?.address}
              locked={!!user?.kyc_verified}
            />
          </div>
        </div>

        {/* Referral Code Section */}
        <div className="bg-gradient-to-r from-[#F5E6C8] to-[#FDEBCF] rounded-xl p-4 border border-[#B8860B]/40 mb-5 animate-in fade-in slide-in-from-left-4 duration-300 delay-150">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#B8860B]/20 flex items-center justify-center border border-[#B8860B]/30">
              <Gift className="w-4 h-4 text-[#B8860B]" />
            </div>
            <h4 className="text-sm font-semibold text-[#004D40]">Your Referral Code</h4>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-white/80 rounded-lg px-4 py-3 border border-[#B8860B]/40">
              <p className="text-lg font-bold tracking-wider text-[#004D40]">{referralCode}</p>
            </div>
            <button
              onClick={copyReferralCode}
              className={`p-3 rounded-lg transition-all ${
                copiedReferral 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-[#004D40] hover:bg-[#00302B]'
              }`}
              title="Copy code"
            >
              {copiedReferral ? (
                <CheckCircle2 className="w-5 h-5 text-white" />
              ) : (
                <Copy className="w-5 h-5 text-white" />
              )}
            </button>
            <button
              onClick={openReferralShareDialog}
              className="p-3 rounded-lg bg-[#B8860B] hover:bg-[#A67C00] transition-all"
              title="Share code"
            >
              <Share2 className="w-5 h-5 text-white" />
            </button>
          </div>
          <p className="text-xs text-[#B8960C] mt-2">Share this code with friends and earn rewards!</p>
        </div>

        {/* Apply Referral Code Section (only for users without transactions) */}
        {canApplyReferral && (
          <div className="bg-white rounded-xl p-4 border border-[#004D40]/30 mb-5 animate-in fade-in slide-in-from-left-4 duration-300 delay-150">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[#004D40]/10 flex items-center justify-center border border-[#004D40]/20">
                <Ticket className="w-4 h-4 text-[#004D40]" />
              </div>
              <h4 className="text-sm font-semibold text-[#004D40]">Have a Referral Code?</h4>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={applyReferralCode}
                onChange={(e) => setApplyReferralCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="flex-1 bg-gray-50 rounded-lg px-4 py-3 border border-[#004D40]/30 text-[#004D40] font-semibold tracking-wider placeholder:text-[#004D40]/40 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#004D40]/50"
                maxLength={12}
              />
              <button
                onClick={handleApplyReferralCode}
                disabled={applyingReferral || !applyReferralCode.trim()}
                className="px-4 py-3 rounded-lg bg-[#004D40] hover:bg-[#00302B] text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {applyingReferral ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>Apply</>
                )}
              </button>
            </div>
            <p className="text-xs text-[#004D40]/70 mt-2">Apply a friend&apos;s code to get bonus on your first booking!</p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white/50 rounded-xl border border-[#D4AF37]/30 mb-5 overflow-hidden animate-in fade-in slide-in-from-left-4 duration-300 delay-200">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className="w-full flex items-center justify-between p-4 hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${action.iconBg} flex items-center justify-center border border-[#D4AF37]/20`}>
                  <action.icon className={`w-4 h-4 ${action.iconColor}`} />
                </div>
                <span className="font-medium text-[#8B7500] text-sm">{action.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {action.badge && (
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    action.badgeType === 'warning' 
                      ? 'text-amber-700 bg-amber-100' 
                      : 'text-green-600 bg-green-50'
                  }`}>
                    {action.badge}
                    {action.badgeType !== 'warning' && <CheckCircle2 className="w-3 h-3" />}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
              </div>
            </button>
          ))}
        </div>

        {/* Terms & Privacy Links */}
        <div className="flex items-center justify-center gap-4 mb-5 animate-in fade-in duration-300 delay-300">
          <button 
            onClick={() => { handleClose(); navigate('/terms-new'); }}
            className="text-xs text-[#B8960C] hover:text-[#8B7500] flex items-center gap-1"
          >
            Terms of Service <ExternalLink className="w-3 h-3" />
          </button>
          <span className="text-[#D4AF37]">|</span>
          <button 
            onClick={() => { handleClose(); navigate('/privacy-new'); }}
            className="text-xs text-[#B8960C] hover:text-[#8B7500] flex items-center gap-1"
          >
            Privacy Policy <ExternalLink className="w-3 h-3" />
          </button>
        </div>

        {/* App Version */}
        <div className="text-center mb-4 animate-in fade-in duration-300 delay-300">
          <p className="text-xs text-[#B8960C]/60">Mint a Milli {appVersion}</p>
        </div>
      </div>

      {/* Fixed Logout Button at Bottom */}
      <div className="flex-shrink-0 p-4 pt-2 border-t border-[#D4AF37]/30 bg-[#F5E6C8]">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 p-3 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-colors"
        >
          <LogOut className="w-5 h-5 text-red-600" />
          <span className="font-medium text-red-600">Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isAnimating && isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Drawer */}
      <div 
        className={`fixed top-0 left-0 h-full w-[380px] max-w-[90vw] bg-[#FAFAF8] shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isAnimating && isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Decorative corner accents */}
        <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-[#004D40]/30 rounded-tl-sm"></div>
        <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-[#004D40]/30 rounded-tr-sm"></div>
        <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-[#004D40]/30 rounded-bl-sm"></div>
        <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-[#004D40]/30 rounded-br-sm"></div>

        {isAuthenticated ? renderProfileUI() : renderLoginUI()}
      </div>

      {/* Bank Details Modal */}
      {showBankModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
            onClick={() => setShowBankModal(false)}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#8B7500]" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {hasBankDetails ? 'Update Bank Details' : 'Add Bank Details'}
                  </h2>
                </div>
                <button 
                  onClick={() => setShowBankModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Form */}
              <div className="p-6 space-y-4">
                {/* Purpose Statement - DPDP Compliance */}
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Purpose:</strong> Your bank details are collected exclusively for processing refunds and withdrawals. 
                    We do not share this information with third parties.
                  </p>
                </div>

                {/* Current Bank Info (if exists) */}
                {hasBankDetails && (
                  <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                    <p className="text-xs text-green-700 mb-1">Current Bank Account</p>
                    <p className="font-semibold text-green-800">
                      {user?.bank_name} - {maskedAccountNumber}
                    </p>
                  </div>
                )}

                {/* Account Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={bankForm.accountNumber}
                      onChange={(e) => handleBankFormChange('accountNumber', e.target.value)}
                      placeholder="Enter account number"
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] transition-colors"
                    />
                  </div>
                </div>

                {/* Confirm Account Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Account Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={bankForm.confirmAccountNumber}
                      onChange={(e) => handleBankFormChange('confirmAccountNumber', e.target.value)}
                      placeholder="Re-enter account number"
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                        bankForm.confirmAccountNumber && bankForm.accountNumber !== bankForm.confirmAccountNumber
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-[#D4AF37]'
                      }`}
                    />
                  </div>
                  {bankForm.confirmAccountNumber && bankForm.accountNumber !== bankForm.confirmAccountNumber && (
                    <p className="text-xs text-red-500 mt-1">Account numbers do not match</p>
                  )}
                </div>

                {/* IFSC Code with Auto-Lookup */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={bankForm.ifscCode}
                      onChange={(e) => handleBankFormChange('ifscCode', e.target.value)}
                      placeholder="e.g., HDFC0001234"
                      maxLength={11}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors uppercase ${
                        ifscError ? 'border-red-300' : 'border-gray-200 focus:border-[#D4AF37]'
                      }`}
                    />
                    {ifscLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
                      </div>
                    )}
                    {!ifscLoading && bankForm.ifscCode.length === 11 && bankForm.bankName && !ifscError && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      </div>
                    )}
                  </div>
                  {ifscLoading && (
                    <p className="text-xs text-[#D4AF37] mt-1">Looking up bank details...</p>
                  )}
                  {ifscError && (
                    <p className="text-xs text-red-500 mt-1">{ifscError}</p>
                  )}
                  {!ifscError && !ifscLoading && (
                    <p className="text-xs text-gray-500 mt-1">Bank name will be auto-filled when you enter a valid IFSC</p>
                  )}
                </div>

                {/* Bank Name - Auto-filled or Manual */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name <span className="text-red-500">*</span>
                    {bankForm.bankName && !ifscError && bankForm.ifscCode.length === 11 && (
                      <span className="ml-2 text-xs text-green-600">(Auto-filled)</span>
                    )}
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={bankForm.bankName}
                      onChange={(e) => setBankForm(prev => ({ ...prev, bankName: e.target.value }))}
                      placeholder="e.g., HDFC Bank"
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] transition-colors"
                    />
                  </div>
                </div>

                {/* DPDP Consent Checkbox */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bankForm.consent}
                      onChange={(e) => setBankForm(prev => ({ ...prev, consent: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37]"
                    />
                    <span className="text-xs text-gray-700 leading-relaxed">
                      I consent to the collection and storage of my bank account details for the purpose of processing refunds and withdrawals. 
                      I understand that I can withdraw this consent and delete my bank details at any time.
                      <a href="/privacy-new" target="_blank" className="text-[#D4AF37] hover:underline ml-1">
                        View Privacy Policy
                      </a>
                    </span>
                  </label>
                </div>

                {/* Important Note */}
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                  <p className="text-xs text-amber-800">
                    <strong>Important:</strong> Ensure the bank account is in your name as per your KYC documents. 
                    Mismatched details may result in failed refunds.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Save Button */}
                  <button
                    onClick={handleSaveBankDetails}
                    disabled={bankLoading || !bankForm.consent}
                    className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8960C] text-white font-semibold rounded-xl hover:from-[#B8960C] hover:to-[#8B7500] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  >
                    {bankLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        {hasBankDetails ? 'Update Bank Details' : 'Save Bank Details'}
                      </>
                    )}
                  </button>

                  {/* Delete Button - Only show if bank details exist */}
                  {hasBankDetails && (
                    <button
                      onClick={handleDeleteBankDetails}
                      disabled={bankLoading}
                      className="w-full py-3 bg-red-50 hover:bg-red-100 text-red-600 font-medium rounded-xl border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                    >
                      <X className="w-4 h-4" />
                      Delete Bank Details
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Referral Share Dialog */}
      <ReferralShareDialog
        isOpen={showReferralDialog}
        onClose={() => setShowReferralDialog(false)}
        referralCode={referralShareData?.referralCode || referralCode}
        shareMessage={referralShareData?.shareMessage}
        shareUrl={referralShareData?.shareUrl}
      />
    </>
  );
};

export default ProfileDrawer;
